import { findClosestMatchFromObject } from '@/lib/search-utils';
import { createContext, useState, useEffect, useMemo } from 'react';
import * as z from 'zod';
import { useUpload } from '../../csv-importer/use-upload';
import { getMetrics } from '../api-helper';
import qualityMetricServiceApi from 'shared/api/quality-metric.service.api';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { useOrganizationContext } from '../../../[id]/organization.context';
import { validDate } from '@/lib/schema.utils';

export const UploadContext = createContext();

const initialOptions = {
  date: 'Date',
  meetsGroupGoal: 'Compliance',
  metric: 'Metric',
  email: 'Email',
  externalId: 'External ID',
};

const qualityMetricOptions = {
  metric: {
    findMatch: null,
    options: [],
  },
};

export const UploadQualityMetricRecordsProvider = ({ children }) => {
  const { organization } = useOrganizationContext();
  const { timezone } = organization;
  const validationSchema = useMemo(
    () =>
      z.object({
        metrics: z.array(
          z
            .object({
              date: z.string().transform(validDate(timezone, 'M/d/yyyy HH:mm:ss')),
              meetsGroupGoal: z.string(),
              email: z.string().email('Invalid email format'),
              metric: z.string().min(2, 'Metric is required'),
              externalId: z.string().optional(),
            })
            .passthrough() // Allow additional unknown fields to metadata field
            .transform(data => {
              const { date, meetsGroupGoal, email, metric, externalId, ...rest } = data;
              return {
                date,
                meetsGroupGoal: meetsGroupGoal === 'true' ? true : false,
                email,
                metric,
                externalId: externalId || nanoid(),
                metadata: rest, // all extra fields go here
              };
            }),
        ),
      }),
    [timezone],
  );

  const [metrics, setMetrics] = useState([]);

  // Fetch rotations
  useEffect(() => {
    getMetrics().then(setMetrics);
  }, []);

  if (metrics.length && !qualityMetricOptions.metric.options.length) {
    // Set the rotation options
    qualityMetricOptions.metric.options = metrics.map(({ id, title }) => ({
      key: id,
      value: title,
    }));

    // Create the metric labels object for searching
    const metricLabels =
      metrics?.reduce((obj, { title, id }) => {
        obj[id] = title;
        return obj;
      }, {}) || {};

    qualityMetricOptions.metric.findMatch = findClosestMatchFromObject(metricLabels);
  }

  const { showResults, stage, headers, ...rest } = useUpload({
    propertiesMap: qualityMetricOptions,
  });

  // Add unknown headers to the property options to be saved in metadata
  const propertyOptions = useMemo(() => {
    const options = { ...initialOptions };
    const existingValues = Object.values(options).map(v => v.toLowerCase());
    headers.forEach(header => {
      if (
        !Object.keys(options).includes(header) &&
        !existingValues.includes(header.toLowerCase())
      ) {
        const normalizedHeader = _.camelCase(header);
        options[normalizedHeader] = header; // Add unknown headers to the options
      }
    });
    return options;
  }, [headers]);

  const onSubmit = async data => {
    try {
      const { metrics } = data;
      // group by metric id
      const groupedRecords = _.groupBy(metrics, 'metric');
      const recordsPerMetric = Object.entries(groupedRecords).map(([metricId, records]) => ({
        metricId,
        records,
      }));

      // send to the api
      const res = await Promise.allSettled(
        recordsPerMetric.map(({ metricId, records }) =>
          qualityMetricServiceApi.createQualityMetricRecords({
            metricId,
            records: records.map(({ date, email, meetsGroupGoal, metadata, externalId }) => ({
              date,
              email,
              meetsGroupGoal,
              metadata,
              externalId,
            })),
          }),
        ),
      );

      const successful = [];
      const failed = [];
      res.forEach(({ status, value }) => {
        if (status === 'fulfilled') {
          const { success, fails } = value.data;
          successful.push(...success);
          failed.push(...fails);
        }
      });

      // Group by userEmail to ensure uniqueness
      const newByEmail = _.uniqBy(successful, 'userEmail').map(({ userEmail }) => ({
        email: userEmail,
        message: 'User quality metrics created successfully',
      }));

      const failedByEmail = Object.entries(_.groupBy(failed, 'userEmail')).map(
        ([email, entries]) => {
          const allRecords = entries.flatMap(e => e.records || []);
          const reasons = _.uniq(entries.map(e => e.reason));
          return {
            email,
            message: (
              <div className="flex flex-col gap-3">
                {reasons.map((reason, i) => (
                  <label key={i}>Reason: {reason}</label>
                ))}
                <ul className="flex flex-col gap-2">
                  {allRecords.map(({ date, meetsGroupGoal }) => (
                    <li key={date} className="flex flex-col gap-1 border border-black p-2 w-fit">
                      <label>Date: {date}</label>
                      <label>Compliance: {meetsGroupGoal ? '✅' : '❌'}</label>
                    </li>
                  ))}
                </ul>
              </div>
            ),
          };
        },
      );

      const results = {
        new: newByEmail,
        updated: [],
        failed: failedByEmail,
      };
      showResults(results);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <UploadContext.Provider
      value={{
        ...rest,
        stage,
        validationSchema,
        propertyOptions,
        headers,
        onSubmit,
        mandatoryFields: ['date', 'metric', 'email', 'meetsGroupGoal'],
        CustomFileUploadRender: () => (
          <div className="mb-4">
            <label className="text-sm text-gray-500">
              Upload a CSV file with the following headers:
            </label>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              <li>Date (format: M/d/yyyy HH:mm:ss)</li>
              <li>Metric (must match an existing metric)</li>
              <li>Email (must be a valid email address)</li>
              <li>Compliance (true/false) - true, means meets the group goal</li>
              <li>External ID (optional, if not provided a random ID will be generated)</li>
            </ul>
            <p className="text-xs text-gray-400 mt-2">
              Note: Additional fields will be stored in the metadata object.
            </p>
          </div>
        ),
      }}>
      {children}
    </UploadContext.Provider>
  );
};
